import { useCruxModuleStore } from '@/stores/api/cruxmodules';
import { useSelectionStore } from '@/stores/ui/selection';
import { camera, initCameraControls } from '@/threejs/core';
import {
  addProjector,
  cleanupProjectorHelperLines,
  getProjectors,
  initProjectorHelperLines,
  removeProjector,
  setHelperLinesEnabled,
  updateProjectorHelperLines,
} from '@/threejs/core/projectorHelperLines';
import { cleanupMeasurementHandler, setUpMeasurementHandler } from '@/threejs/handlers/measurement';
import { setUpTransformControls } from '@/threejs/handlers/transform';
import { animationManager } from '@/threejs/managers/animationManager';
import { backgroundManager } from '@/threejs/managers/backgroundManager';
import { edgesManager } from '@/threejs/managers/edgesManager';
import { gridManager } from '@/threejs/managers/gridManager';
import { lightingManager } from '@/threejs/managers/lightingManager';
import { materialManager } from '@/threejs/managers/materialManager';
import { projectorManager } from '@/threejs/managers/projectorManager';
import { shouldSkipObject } from '@/threejs/objectManager';
import { OutlinePass } from '@/threejs/shaders/OutlinePass';
import { FrustumEdgePass } from '@/threejs/shaders/projection/frustum-edge-pass';
import { FrustumOverlayPass } from '@/threejs/shaders/projection/frustum-overlay-pass';
import { ProjectorFrustumEdge } from '@/threejs/shaders/projection/projector-frustum-edge';
import { ProjectorFrustumOverlay } from '@/threejs/shaders/projection/projector-frustum-overlay';
import { OutputPass } from 'three/examples/jsm/postprocessing/OutputPass.js';
import { FXAAShader } from 'three/examples/jsm/shaders/FXAAShader.js';
import { GammaCorrectionShader } from 'three/examples/jsm/shaders/GammaCorrectionShader.js';

import { FrustumGradientPass } from '@/threejs/shaders/projection/frustum-gradient-pass';
import { FrustumOverlapPass } from '@/threejs/shaders/projection/frustum-overlap-pass';
import { FrustumPixelSizePass } from '@/threejs/shaders/projection/frustum-pixel-size-pass';
import { ProjectorFrustumGradientOverlay } from '@/threejs/shaders/projection/projector-frustum-gradient-overlay';
import { ProjectorFrustumOverlap } from '@/threejs/shaders/projection/projector-frustum-overlap';
import { ProjectorFrustumPixelSize } from '@/threejs/shaders/projection/projector-frustum-pixel-size';

import { ViewHelper } from '@tonybfox/threejs-tools';
import * as THREE from 'three';
import { CSS2DRenderer } from 'three/addons/renderers/CSS2DRenderer.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { ref, watch } from 'vue';
import { renderer, updateRendererSize } from './renderer';

// Create the singleton scene
export let scene = new THREE.Scene();
export let viewHelper = null;
//window.scene = scene
export const hasSceneLoaded = ref(false);

// Reactive scene lux stats for UI components
export const sceneLuxStats = ref({ min: 0, max: 0 });

// Initialize CSS2DRenderer
export const labelRenderer = new CSS2DRenderer();
labelRenderer.setSize(window.innerWidth, window.innerHeight);
labelRenderer.domElement.style.position = 'absolute';
labelRenderer.domElement.style.top = '0px';
labelRenderer.domElement.style.pointerEvents = 'none';

// add grid
gridManager.initializeGrid();
edgesManager.initializeEdges(scene);

// Post-processing setup
export let composer;
let outlinePass;
let renderPass;
const gizmoLayer = 1;
const gizmoScene = new THREE.Scene();

let sceneInitialized = false;

const initialLuxReferenceDistance = 10.0;
const initialLuxMax = 0; // Increased from 0 to make overlay visible
const initialGradientLuxMax = 600; // Set to match actual projector output for accurate brightness
const initialLuxLogScale = false;
let photometricPostProcessPass;
let edgeDetectionPass;
let gridMesh = null;
let animationFrameId = null;
let containerRef = null;

let projectors = getProjectors();

// Multi-pass shadow renderer
let forceRender = false;

const clock = new THREE.Clock();

// Camera controls
let cameraControls = null;

// Frustum edge pass
let frustumEdgePass = null;
let frustumGradientPass = null;
let frustumOverlayPass = null;
let frustumOverlapPass = null;
let frustumPixelSizePass = null;

let frustumEdge = null;
let gradientOverlay = null;
let frustumOverlay = null;
let frustumPixelSize = null;
let frustumOverlap = null;

// Store watcher stop handles for cleanup
let watcherStopHandles = [];
let eventListenerCleanups = [];

const maxProjectorCount = 50;

const useTextureArray = renderer.capabilities.isWebGL2;
const maxProjectorSamplers = Math.max(1, renderer.capabilities.maxTextures - 4);
// Account for multiple overlay shaders stacking uniforms on the same materials
// (overlay, gradient, overlap, edge, pixel size - each adds ~6-10 vec4 per projector)
const uniformVectorsPerProjector = 35;
const maxProjectorsByUniforms = Math.max(
  1,
  Math.floor((renderer.capabilities.maxFragmentUniforms - 100) / uniformVectorsPerProjector)
);
// Cap at 8 projectors to ensure we stay under uniform limits with all overlays active
const maxProjectors = Math.min(
  8,
  maxProjectorCount,
  useTextureArray ? maxProjectorsByUniforms : Math.min(maxProjectorSamplers, maxProjectorsByUniforms)
);
if (!useTextureArray) {
  console.warn('WebGL2 not available. Falling back to multi-pass overlay with sampler limits.');
}

export { addProjector, removeProjector };

export function updatePhotometricHoverIntensity() {}

export let measurementHandler;

// Lazy initialization of store to avoid calling before Pinia is ready
let _cruxModuleStore = null;
function getCruxModuleStore() {
  if (!_cruxModuleStore) {
    _cruxModuleStore = useCruxModuleStore();
  }
  return _cruxModuleStore;
}

// Helper functions to get settings from cruxModuleStore
function getViewSetting(key, defaultValue = null) {
  return getCruxModuleStore().getSettingValue('viewport_settings', key, defaultValue);
}

// Resize handler
function handleResize() {
  if (!containerRef) return;
  updateSceneSize(containerRef);
}

// Initialize scene with container
export function initScene(container) {
  sceneInitialized = true;
  projectors = getProjectors();
  if (scene === null) {
    scene = new THREE.Scene();
  }
  if (!container) {
    console.error('Container element is required');
    return { scene, camera };
  }

  composer = new EffectComposer(renderer);

  // Store container reference
  containerRef = container;

  // add grid
  gridManager.initializeGrid();
  edgesManager.initializeEdges(scene);

  // Check if container has dimensions
  const { clientWidth, clientHeight } = container;
  if (clientWidth === 0 || clientHeight === 0) {
    console.error('Container has zero dimensions', {
      clientWidth,
      clientHeight,
    });

    return { scene, camera };
  }

  // Append renderers to container
  container.appendChild(renderer.domElement);
  container.appendChild(labelRenderer.domElement);

  cameraControls = initCameraControls(container);

  // Enable gizmo layer on camera so it can see helper lines
  camera.layers.enable(gizmoLayer);

  // Initialize projector helper lines with scene references
  initProjectorHelperLines(scene, gizmoScene, gizmoLayer, camera);

  // Initialize animation manager with scene
  animationManager.initialize(scene);

  viewHelper = new ViewHelper(camera, renderer.domElement, {
    size: 128,
    position: 'bottom-right',
    offset: {
      x: 250,
      y: 10,
    },
    center: new THREE.Vector3(0, 0, 0),
    controls: cameraControls,
  });

  // Setup CAD environment
  setupCADEnvironment();

  frustumOverlay = new ProjectorFrustumOverlay({
    maxProjectors,
    useTextureArray,
    fillOpacity: 1,
    facingThreshold: 0,
    luxSpectrum: true,
    luxReferenceDistance: initialLuxReferenceDistance,
    luxMax: initialLuxMax,
    luxLogScale: initialLuxLogScale,
  });
  frustumOverlay.applyTo(scene);

  gradientOverlay = new ProjectorFrustumGradientOverlay({
    maxProjectors,
    useTextureArray,
    gradientColor: 0xffffff,
    gradientOpacity: 1,
    facingThreshold: 0,
    luxReferenceDistance: initialLuxReferenceDistance,
    luxMax: initialGradientLuxMax,
    luxLogScale: initialLuxLogScale,
    projectionTextureStrength: 1.0,
  });
  gradientOverlay.applyTo(scene);

  frustumEdge = new ProjectorFrustumEdge({
    maxProjectors,
    useTextureArray,
    color: 0x86e6ff,
    edge: 1.8,
    facingThreshold: 0,
  });

  frustumOverlap = new ProjectorFrustumOverlap({
    maxProjectors,
    useTextureArray,
    overlapOpacity: 1,
    facingThreshold: 0,
  });

  // Use smaller maxProjectors for pixel size to stay under uniform limit
  // (all overlays share the same materials and their uniforms accumulate)
  frustumPixelSize = new ProjectorFrustumPixelSize({
    maxProjectors: 4,
    warningOpacity: 0.6,
    idealPixelSize: 0.003, // 3mm default
    facingThreshold: 0,
  });
  frustumPixelSize.applyTo(scene);

  const renderPass = new RenderPass(scene, camera);
  composer.addPass(renderPass);
  frustumGradientPass = new FrustumGradientPass(scene, camera, gradientOverlay, projectors);
  composer.addPass(frustumGradientPass);

  frustumOverlayPass = new FrustumOverlayPass(scene, camera, frustumOverlay, projectors);
  composer.addPass(frustumOverlayPass);

  frustumPixelSizePass = new FrustumPixelSizePass(scene, camera, frustumPixelSize, projectors);
  composer.addPass(frustumPixelSizePass);

  frustumOverlapPass = new FrustumOverlapPass(scene, camera, frustumOverlap, projectors);
  composer.addPass(frustumOverlapPass);

  frustumEdgePass = new FrustumEdgePass(scene, camera, frustumEdge, projectors);

  outlinePass = new OutlinePass(new THREE.Vector2(window.innerWidth, window.innerHeight), scene, camera);
  outlinePass.edgeStrength = 2.8;
  outlinePass.edgeGlow = 0.15;
  outlinePass.edgeThickness = 1.2;
  outlinePass.visibleEdgeColor.set(0xffffff);
  outlinePass.hiddenEdgeColor.set(0xffffff);
  outlinePass.selectedObjects = [];
  composer.addPass(outlinePass);

  composer.addPass(frustumEdgePass);

  const fxaaPass = new ShaderPass(FXAAShader);
  //composer.addPass(fxaaPass)

  const outputPass = new OutputPass();
  composer.addPass(outputPass);

  const gammaCorrectionPass = new ShaderPass(GammaCorrectionShader);
  gammaCorrectionPass.renderToScreen = false;
  //composer.addPass(gammaCorrectionPass)

  const selectionStore = useSelectionStore();
  watch(
    () => selectionStore.selectedObjectsWithChildren,
    (newObjects) => {
      // Convert proxy objects to raw Three.js objects
      const rawObjects = newObjects
        .map((obj) => {
          if (obj && obj.__v_raw) {
            return obj.__v_raw;
          }
          return obj;
        })
        .filter((obj) => obj !== null && obj !== undefined)
        .filter((obj) => {
          let current = obj;
          while (current) {
            if (current.userData?.type === 'measurement') return false;
            current = current.parent;
          }
          return true;
        });
      if (outlinePass) outlinePass.selectedObjects = rawObjects;
      render();
    }
  );

  // Listen for camera changes and update RenderPass
  const cameraChangedHandler = (event) => {
    const newCamera = event.detail.camera;
    renderPass.camera = newCamera;
    outlinePass.renderCamera = newCamera;
    newCamera.updateProjectionMatrix();
  };
  window.addEventListener('cameraChanged', cameraChangedHandler);
  eventListenerCleanups.push(() => window.removeEventListener('cameraChanged', cameraChangedHandler));

  const renderHandler = (event) => {
    render();
  };
  window.addEventListener('render', renderHandler);
  eventListenerCleanups.push(() => window.removeEventListener('render', renderHandler));

  const objectUpdatedHandler = (event) => {
    // Apply overlays to the updated object if provided
    if (event.detail?.object) {
      applyOverlaysToObject(event.detail.object);
    }
  };
  window.addEventListener('objectUpdated', objectUpdatedHandler);
  eventListenerCleanups.push(() => window.removeEventListener('objectUpdated', objectUpdatedHandler));

  const objectAddedHandler = (event) => {
    // Apply overlays to the newly added object if provided
    if (event.detail?.object) {
      applyOverlaysToObject(event.detail.object);
    }
  };
  window.addEventListener('objectAdded', objectAddedHandler);
  eventListenerCleanups.push(() => window.removeEventListener('objectAdded', objectAddedHandler));

  const stopViewWatcher = watch(
    () => getViewSetting('render.view'),
    (newView, oldView) => {
      // Guard against null overlays
      if (!frustumOverlay || !frustumPixelSize || !gradientOverlay) return;

      switch (newView) {
        default:
          frustumOverlay.clearOverlay();
          frustumPixelSize.clearOverlay();
          frustumGradientPass?.setEnabled(true);
          frustumOverlayPass?.setEnabled(false);
          frustumPixelSizePass?.setEnabled(false);
          break;
        case 'none':
          gradientOverlay.clearOverlay();
          frustumOverlay.clearOverlay();
          frustumPixelSize.clearOverlay();
          frustumGradientPass?.setEnabled(false);
          frustumOverlayPass?.setEnabled(false);
          frustumPixelSizePass?.setEnabled(false);
          break;
        case 'photometric':
          gradientOverlay.clearOverlay();
          frustumPixelSize.clearOverlay();
          frustumGradientPass?.setEnabled(false);
          frustumOverlayPass?.setEnabled(true);
          frustumPixelSizePass?.setEnabled(false);
          break;
        case 'pixelSize':
          gradientOverlay.clearOverlay();
          frustumOverlay.clearOverlay();
          frustumGradientPass?.setEnabled(false);
          frustumOverlayPass?.setEnabled(false);
          frustumPixelSizePass?.setEnabled(true);
          break;
      }

      render();
    },
    { immediate: true }
  );
  watcherStopHandles.push(stopViewWatcher);

  const stopOverlapsWatcher = watch(
    () => getViewSetting('render.overlaps'),
    (newValue) => {
      if (!frustumOverlapPass || !frustumOverlap) return;
      frustumOverlapPass.setEnabled(newValue);
      frustumOverlap.clearOverlap();
      render();
    }
  );
  watcherStopHandles.push(stopOverlapsWatcher);

  const stopEdgesWatcher = watch(
    () => getViewSetting('render.frustumEdges'),
    (newValue) => {
      if (!frustumEdgePass) return;
      frustumEdgePass.setEdgeEnabled(newValue);
      setHelperLinesEnabled(newValue);
      render();
    }
  );
  watcherStopHandles.push(stopEdgesWatcher);

  const stopPixelSizeWatcher = watch(
    () => getViewSetting('render.idealPixelSize'),
    (newValue) => {
      if (frustumPixelSize && newValue !== undefined) {
        // Convert from mm to meters
        frustumPixelSize.setIdealPixelSize(newValue / 1000);
        render();
      }
    }
  );
  watcherStopHandles.push(stopPixelSizeWatcher);

  // Add resize listener
  window.addEventListener('resize', handleResize);
  eventListenerCleanups.push(() => window.removeEventListener('resize', handleResize));

  updateSceneSize(container);

  setUpTransformControls(scene, camera, cameraControls);
  measurementHandler = setUpMeasurementHandler();

  // Initialize edges manager with scene

  animate();

  return { scene, camera, cameraControls };
}

// Function to apply overlays to a specific object and its children
export function applyOverlaysToObject(object) {
  if (!gradientOverlay || !frustumOverlay || !frustumPixelSize || !frustumOverlap) {
    return;
  }

  if (!sceneInitialized) {
    return;
  }
  // Apply ALL overlays to the object tree regardless of which pass is enabled.
  // The overlays just inject shader code - they don't render anything unless
  // their uniforms are set up. This ensures switching views works correctly.
  frustumOverlap.applyTo(object);
  frustumOverlay.applyTo(object);
  gradientOverlay.applyTo(object);
  frustumPixelSize.applyTo(object);
  forceRender = true;
  animate();
}

// Legacy function - now just applies to new objects
export function applyOverlaysToNewMeshes() {
  // This is less efficient but ensures compatibility
  if (!gradientOverlay || !frustumOverlay || !frustumPixelSize || !frustumOverlap) {
    return;
  }

  scene.traverse((object) => {
    applyOverlaysToObject(object);
  });
}

// Setup CAD-like environment with grid, axes, and sample objects
function setupCADEnvironment() {
  // Basic lights
  const ambientLight = new THREE.AmbientLight(0xffffff, 0);
  ambientLight.visible = false;
  scene.add(ambientLight);

  // Main directional light
  const dirLight1 = new THREE.DirectionalLight(0xffffff, 0);
  dirLight1.position.set(-10, 16, 7);
  dirLight1.castShadow = true;

  // Increase shadow camera size
  dirLight1.shadow.camera = new THREE.OrthographicCamera(-35, 35, 35, -35, 0.1, 1000);
  //dirLight1.shadow.bias = -0.0001
  // dirLight1.shadow.normalBias = 0.04
  dirLight1.shadow.mapSize.width = 2048;
  dirLight1.shadow.mapSize.height = 2048;
  dirLight1.visible = false;
  scene.add(dirLight1);

  // Secondary directional light
  const dirLight2 = new THREE.DirectionalLight(0xffffff, 0);
  dirLight2.position.set(5, 10, -10);
  dirLight2.castShadow = true;
  dirLight2.shadow.camera = new THREE.OrthographicCamera(-15, 15, 15, -15, 0.1, 1000);
  //dirLight2.shadow.bias = -0.0001
  // dirLight2.shadow.normalBias = 0.04
  dirLight2.visible = false;
  scene.add(dirLight2);
}

const raycaster = new THREE.Raycaster();
raycaster.layers.set(0);
const visibilityRaycaster = new THREE.Raycaster();
visibilityRaycaster.layers.set(0);

const sceneSampleGrid = [-1, -0.8, -0.6, -0.4, -0.2, 0, 0.2, 0.4, 0.6, 0.8, 1];
const tempPosition = new THREE.Vector3();
const tempDirection = new THREE.Vector3();
const tempNdc = new THREE.Vector3();

const _occlusionFrustum = new THREE.Frustum();
const _occlusionMatrix = new THREE.Matrix4();
const _annotationWorldPos = new THREE.Vector3();

function _checkAnnotationOcclusion(obj) {
  if (obj.userData.type !== 'annotation') return;

  obj.target?.getWorldPosition(_annotationWorldPos);

  let occluded = false;
  for (const projector of projectors) {
    if (!projector.camera) continue;
    _occlusionMatrix.multiplyMatrices(projector.camera.projectionMatrix, projector.camera.matrixWorldInverse);
    _occlusionFrustum.setFromProjectionMatrix(_occlusionMatrix);
    if (_occlusionFrustum.containsPoint(_annotationWorldPos)) {
      occluded = true;
      break;
    }
  }
  obj.setOccluded?.(occluded);
}

function updateAnnotationOcclusion() {
  for (const projector of projectors) {
    projector.camera?.updateMatrixWorld();
  }
  scene.traverse(_checkAnnotationOcclusion);
}

export function animate() {
  if (!cameraControls) return;
  if (!sceneInitialized) return;

  const delta = clock.getDelta();
  const hasControlsUpdated = cameraControls.update(delta);
  if (hasControlsUpdated || forceRender) {
    forceRender = false;

    if (viewHelper) {
      viewHelper.update(delta);
    }

    updateProjectorHelperLines();
    updateAnnotationOcclusion();

    // Calculate actual scene lux stats for photometric overlay
    if (frustumOverlayPass.getEnabled() && projectors.length > 0) {
      let stats = getSceneLuxStatsApprox();
      if (stats.max > 0) {
        // Set the actual scene max lux BEFORE updateLuxStats so it uses real values
        frustumOverlay.setLuxMax(stats.max);
      }
      // Update reactive ref for UI components
      sceneLuxStats.value = stats;
    }

    frustumOverlay.updateLuxStats(projectors);

    if (frustumOverlapPass.getEnabled()) {
      frustumOverlap.clearOverlap();
    }

    // Only clear overlays for passes that are enabled
    if (frustumOverlayPass.getEnabled()) {
      frustumOverlay.clearOverlay();
    }
    if (frustumGradientPass.getEnabled()) {
      gradientOverlay.clearOverlay();
    }
    if (frustumPixelSizePass.getEnabled()) {
      frustumPixelSize.clearOverlay();
    }
    frustumEdge.clearEdges();

    composer.render();

    // Render CSS2D labels/sprites
    labelRenderer.render(scene, camera);

    const previousAutoClear = renderer.autoClear;
    renderer.autoClear = false;
    renderer.clearDepth();
    renderer.render(gizmoScene, camera);
    renderer.autoClear = previousAutoClear;
  }

  requestAnimationFrame(animate);
}
export function render() {
  forceRender = true;
}

// Update scene size based on container
export function updateSceneSize(container) {
  if (!container) {
    console.error('Container is required for updateSceneSize');
    return;
  }

  const width = container.clientWidth;
  const height = container.clientHeight;
  if (width === 0 || height === 0) {
    console.warn('Container has zero dimensions in updateSceneSize', {
      width,
      height,
    });
    return;
  }

  // Update renderer size
  updateRendererSize(container);

  // Update label renderer size
  labelRenderer.setSize(width, height);

  // Update camera aspect ratio
  camera.aspect = width / height;
  camera.updateProjectionMatrix();

  // Update composer size
  if (composer) {
    composer.setSize(width, height);
    composer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  }

  // Update outline pass size
  if (outlinePass) {
    outlinePass.resolution.set(width, height);
  }

  // Update edge detection pass size
  if (edgeDetectionPass) {
    edgeDetectionPass.uniforms.resolution.value.set(
      width * Math.min(window.devicePixelRatio, 2),
      height * Math.min(window.devicePixelRatio, 2)
    );
  }

  if (composer) {
    if (frustumEdge) {
      frustumEdge.clearEdges();
    }
    composer.render();
  }
}

// Cleanup function to dispose of resources
export function cleanupScene() {
  sceneInitialized = false;

  // Stop all watchers
  watcherStopHandles.forEach((stop) => stop());
  watcherStopHandles = [];

  // Remove all event listeners
  eventListenerCleanups.forEach((cleanup) => cleanup());
  eventListenerCleanups = [];

  // Clear container reference
  containerRef = null;

  // Stop animation loop
  if (animationFrameId) {
    cancelAnimationFrame(animationFrameId);
    animationFrameId = null;
  }

  // Clean up grid
  if (gridMesh) {
    scene.remove(gridMesh);
    gridMesh = null;
  }

  // Clean up projector helper lines
  cleanupProjectorHelperLines();

  // Clean up composer
  if (composer) {
    composer.dispose();
    composer = null;
  }

  // Clean up label renderer - remove from DOM if it exists
  if (labelRenderer && labelRenderer.domElement && labelRenderer.domElement.parentNode) {
    labelRenderer.domElement.parentNode.removeChild(labelRenderer.domElement);
  }

  // Clean up measurement handler
  if (measurementHandler) {
    cleanupMeasurementHandler();
    measurementHandler = null;
  }

  // Clean up animation manager
  animationManager.dispose();

  // Clean up edges manager
  edgesManager.dispose();

  // Clean up edge detection pass
  if (edgeDetectionPass) {
    edgeDetectionPass = null;
  }

  // Clean up outline pass
  if (outlinePass) {
    outlinePass.dispose();
    outlinePass = null;
  }

  // Clean up render pass
  if (renderPass) {
    renderPass.dispose();
    renderPass = null;
  }

  // Clean up frustum passes
  if (frustumEdgePass) {
    frustumEdgePass = null;
  }
  if (frustumGradientPass) {
    frustumGradientPass = null;
  }
  if (frustumOverlayPass) {
    frustumOverlayPass = null;
  }
  if (frustumOverlapPass) {
    frustumOverlapPass = null;
  }
  if (frustumPixelSizePass) {
    frustumPixelSizePass = null;
  }

  // Clean up frustum overlays
  frustumEdge = null;
  gradientOverlay = null;
  frustumOverlay = null;
  frustumPixelSize = null;
  frustumOverlap = null;

  // Clean up view helper
  if (viewHelper) {
    viewHelper.dispose?.();
    viewHelper = null;
  }

  // Clean up grid manager
  gridManager.dispose();

  // Clean up lighting manager
  lightingManager.dispose();

  // Clean up material manager
  materialManager.dispose();

  // Clean up projector manager
  projectorManager.reset();

  // Clean up background manager and reset scene background
  backgroundManager.dispose();
  scene.background = null;

  // Dispose of all objects in gizmoScene
  gizmoScene.traverse((object) => {
    if (object.geometry) {
      object.geometry.dispose();
    }
    if (object.material) {
      object.material.dispose();
    }
  });
  gizmoScene.clear();

  // Dispose of all objects and materials in the scene
  scene.traverse((object) => {
    // Clean up CSS2DObject DOM elements
    if (object.isCSS2DObject && object.element) {
      if (object.element.parentNode) {
        object.element.parentNode.removeChild(object.element);
      }
    }
    if (object.geometry) {
      object.geometry.dispose();
    }
    if (object.material) {
      const materials = Array.isArray(object.material) ? object.material : [object.material];
      for (const material of materials) {
        if (material.map) material.map.dispose();
        if (material.lightMap) material.lightMap.dispose();
        if (material.bumpMap) material.bumpMap.dispose();
        if (material.normalMap) material.normalMap.dispose();
        if (material.specularMap) material.specularMap.dispose();
        if (material.envMap) material.envMap.dispose();
        material.dispose();
      }
    }
  });

  // Clear scene
  scene.clear();

  // Reset scene loaded state
  hasSceneLoaded.value = false;

  // Reset scene lux stats
  sceneLuxStats.value = { min: 0, max: 0 };

  // Clear any remaining global references
  window.scene = null;
}

const getSceneLuxStatsApprox = () => {
  if (projectors.length === 0) {
    return { min: 0, max: 0 };
  }

  const samplePoints = [];

  for (const projector of projectors) {
    projector.updateMatrixWorld(true);
    projector.camera.updateMatrixWorld(true);
    for (const y of sceneSampleGrid) {
      for (const x of sceneSampleGrid) {
        raycaster.setFromCamera({ x, y }, projector.camera);
        const hits = raycaster.intersectObjects(scene.children, true);
        const hit = hits.find((h) => !shouldSkipObject(h.object));
        if (hit) {
          samplePoints.push({ point: hit.point.clone(), object: hit.object });
        }
      }
    }
  }

  if (samplePoints.length === 0) {
    return { min: 0, max: 0 };
  }

  let minLux = Number.POSITIVE_INFINITY;
  let maxLux = 0;

  for (const sample of samplePoints) {
    let luxSum = 0;
    for (const projector of projectors) {
      if (!isPointInProjectorFrustum(projector, sample.point)) {
        continue;
      }
      if (!isPointVisibleFromProjector(projector, sample.point, sample.object)) {
        continue;
      }
      const distance = sample.point.distanceTo(projector.camera.getWorldPosition(tempPosition));
      luxSum += getProjectorLuxAtDistance(projector, distance);
    }
    minLux = Math.min(minLux, luxSum);
    maxLux = Math.max(maxLux, luxSum);
  }

  return {
    min: Number.isFinite(minLux) ? minLux : 0,
    max: maxLux,
  };
};

const isPointInProjectorFrustum = (projector, point) => {
  tempNdc.copy(point).project(projector.camera);
  return tempNdc.x >= -1 && tempNdc.x <= 1 && tempNdc.y >= -1 && tempNdc.y <= 1 && tempNdc.z >= -1 && tempNdc.z <= 1;
};

const isPointVisibleFromProjector = (projector, point, object) => {
  projector.camera.getWorldPosition(tempPosition);
  tempDirection.copy(point).sub(tempPosition);
  const distance = tempDirection.length();
  if (distance <= 0.0001) {
    return false;
  }
  tempDirection.normalize();
  visibilityRaycaster.set(tempPosition, tempDirection);
  visibilityRaycaster.far = distance + 0.02;
  const objects = [];
  scene.traverse((h) => {
    if (!shouldSkipObject(h)) {
      objects.push(h);
    }
  });
  const hits = visibilityRaycaster.intersectObjects(objects, false);
  if (hits.length === 0) {
    return false;
  }
  const first = hits[0];
  return first.object === object && Math.abs(first.distance - distance) < 0.05;
};

const getProjectorLuxFactor = (projector) => {
  const fovY = THREE.MathUtils.degToRad(projector.camera.fov);
  const tanY = Math.tan(fovY / 2);
  const tanX = tanY * projector.camera.aspect;
  const denominator = Math.max(4 * tanX * tanY, 1e-6);

  const stacked = Math.max(1, projector.userData?.stackedProjectors ?? 1);
  return (
    (Math.max(0, projector.userData.lumens) * Math.max(0, projector.userData.power_brightness) * stacked) / denominator
  );
};

const getProjectorLuxAtDistance = (projector, distance) => {
  const factor = getProjectorLuxFactor(projector);
  const distanceSq = Math.max(distance * distance, 1e-4);
  return factor / distanceSq;
};
